import React from 'react';
import { Link } from 'react-router-dom';
import { Scroll } from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import FloatingActions from '../components/FloatingActions';
import Footer from '../components/Footer';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-heading text-2xl sm:text-3xl text-gold mb-3">{title}</h2>
    <div className="text-text-main text-[15px] leading-[1.9] space-y-3">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      <SEO
        title="Terms of Service | ViratAstro"
        description="ViratAstro की Terms of Service — सेवा उपयोग की शर्तें, भुगतान नीति, अस्वीकरण, बौद्धिक संपदा।"
        path="/terms"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'Terms', path: '/terms' },
        ]}
      />
      <Header />
      <section className="pt-28 pb-16">
        <div className="container-custom max-w-3xl">
          <Link to="/" className="text-sm text-text-muted hover:text-gold mb-4 inline-block">← होम</Link>

          <div className="flex items-center gap-3 mb-3">
            <Scroll className="w-8 h-8 text-gold" />
            <h1 className="font-heading text-4xl sm:text-5xl text-text-main">Terms of Service</h1>
          </div>
          <p className="text-text-muted text-sm mb-2">सेवा की शर्तें · कृपया उपयोग से पहले ध्यान से पढ़ें।</p>
          <p className="text-text-muted/70 text-xs mb-8">Last updated: 13 May 2026</p>

          <Section title="1. सेवा की प्रकृति">
            <p>ViratAstro एक <strong className="text-gold">वैदिक ज्योतिष परामर्श</strong> मंच है, जो आचार्य Vivek Mudgal (33+ वर्षों के अनुभव) के मार्गदर्शन में पराशर ऋषि के सिद्धांतों पर आधारित विश्लेषण प्रदान करता है।</p>
            <p>हमारी सेवाएँ <strong>आध्यात्मिक मार्गदर्शन एवं शास्त्रीय अध्ययन हेतु</strong> हैं। यह चिकित्सीय, कानूनी, वित्तीय, या निवेश सलाह का स्थानापन्न नहीं है।</p>
          </Section>

          <Section title="2. भुगतान नीति (अति महत्वपूर्ण)">
            <div className="bg-gold/10 border-2 border-gold/40 rounded-xl p-5">
              <p className="font-bold text-gold mb-2">⚠️ कृपया भुगतान से पहले पढ़ें:</p>
              <p className="text-base">
                <strong>यह वैदिक विश्लेषण सेवा स्वचालित डिजिटल मार्गदर्शन पर आधारित है।</strong>
                <br /><br />
                <strong>विश्लेषण प्रदान होने के पश्चात सेवा पूर्ण मानी जाएगी</strong> — किसी भी प्रकार की भुगतान वापसी का प्रावधान नहीं है।
              </p>
            </div>
            <p>यह नीति निम्न कारणों से है:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>विश्लेषण के तुरंत बाद digital service render हो जाती है</li>
              <li>हर answer आपकी unique कुंडली पर customized है</li>
              <li>दुरुपयोग रोकने हेतु यह आवश्यक है</li>
            </ul>
            <p>यदि आपको अपने विश्लेषण से सम्बंधित कोई शास्त्रीय स्पष्टीकरण चाहिए, आप dashboard में Support विकल्प का प्रयोग कर सकते हैं।</p>
          </Section>

          <Section title="3. ज्योतिष अस्वीकरण">
            <p><strong className="text-gold">ज्योतिषीय विश्लेषण ग्रह स्थितियों एवं संभावनाओं पर आधारित वैदिक मार्गदर्शन है। अंतिम निर्णय सदैव अपने विवेक एवं योग्य वैदिक ज्योतिषी के मार्गदर्शन से ही लें।</strong></p>
            <p>हम कोई "गारंटीशुदा भविष्यवाणी" नहीं देते। ज्योतिष एक मार्गदर्शक है, सर्वथा अंतिम सत्य नहीं। आपके कर्म एवं विवेक ही जीवन की दिशा निर्धारित करते हैं।</p>
          </Section>

          <Section title="4. आयु सीमा">
            <p>आपको हमारी सेवा का उपयोग करने हेतु <strong>18 वर्ष से अधिक</strong> होना चाहिए। नाबालिगों की कुंडली विश्लेषण माता-पिता की लिखित सहमति से ही होगा।</p>
          </Section>

          <Section title="5. उपयोग की सीमाएँ">
            <p>आप वचन देते हैं कि आप:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>गलत जन्म विवरण नहीं देंगे</li>
              <li>किसी और की जानकारी unauthorized रूप से नहीं डालेंगे</li>
              <li>सेवा का उपयोग कानूनी उद्देश्यों के लिए ही करेंगे</li>
              <li>Content को बिना अनुमति copy/redistribute नहीं करेंगे</li>
            </ul>
          </Section>

          <Section title="6. बौद्धिक संपदा">
            <p>ViratAstro पर समस्त content — articles, blog posts, FAQ answers, AI-generated विश्लेषण — का बौद्धिक स्वामित्व ViratAstro/आचार्य Vivek Mudgal के पास है।</p>
            <p>व्यक्तिगत उपयोग हेतु आप अपने विश्लेषण का प्रिंट ले सकते हैं, पर commercial republish वर्जित है।</p>
          </Section>

          <Section title="7. सेवा उपलब्धता">
            <p>हम सर्वश्रेष्ठ uptime का प्रयास करते हैं, किंतु तकनीकी maintenance हेतु अस्थायी रूप से service unavailable हो सकती है। आपातकाल में email द्वारा सूचित किया जाएगा।</p>
          </Section>

          <Section title="8. क्षतिपूर्ति सीमा">
            <p>किसी भी परिस्थिति में हमारी जिम्मेदारी आपके द्वारा भुगतान की गई राशि से अधिक नहीं होगी।</p>
          </Section>

          <Section title="9. कानून का अधिकार-क्षेत्र">
            <p>ये Terms <strong>भारतीय कानून</strong> के अंतर्गत आती हैं। किसी भी विवाद का निपटारा <strong>Faridabad, हरियाणा</strong> के न्यायालयों में होगा।</p>
            <p>UAE/अन्य देश के users यह स्वीकार करते हैं कि dispute resolution Indian jurisdiction में होगा।</p>
          </Section>

          <Section title="10. Terms में बदलाव">
            <p>हम Terms में बदलाव का अधिकार रखते हैं। महत्वपूर्ण परिवर्तनों की सूचना email/WhatsApp/homepage banner द्वारा दी जाएगी।</p>
          </Section>

          <Section title="11. संपर्क">
            <p>किसी भी प्रश्न हेतु: <span className="text-gold">support@viratastro.cloud</span></p>
            <p>WhatsApp: +91-9311369729</p>
            <p>Address: PB-77 GF Parklands Pride, Faridabad, Haryana 121004, India</p>
          </Section>

          <div className="text-center text-text-muted text-xs italic mt-12">
            "सत्यमेव जयते — हम पारदर्शिता को शास्त्र की तरह पवित्र मानते हैं।"
          </div>
        </div>
      </section>
      <FloatingActions />
      <Footer />
    </div>
  );
}
